import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
// Date picker imports removed for simplicity
import './App.css';

// Import pages
import Verify from './pages/Verify';
import VerifyOrganization from './pages/VerifyOrganization';
import VerifyPersonnel from './pages/VerifyPersonnel';
// AllVerifiedPersonnel removed - using VerifiedPersonnel instead
import NotFound from './pages/NotFound';
import Login from './pages/Login';
// Register page removed for security
import ForgotPassword from './pages/ForgotPassword';
import AdminDashboard from './pages/AdminDashboard';
import AddCertificate from './pages/AddCertificate';
import AccreditedOrganizations from './pages/AccreditedOrganizations';
import AddAccreditedOrganization from './pages/AddAccreditedOrganization';
import VerifiedPersonnel from './pages/VerifiedPersonnel';
import AddVerifiedPersonnel from './pages/AddVerifiedPersonnel';
import CertificatesList from './pages/CertificatesList';

// Import custom theme
import theme from './theme/theme';

// Import Firebase service
import FirebaseService from './services/FirebaseService';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  
  useEffect(() => {
    // Check authentication status
    const checkAuth = () => {
      const isAuth = FirebaseService.isAuthenticated();
      setAuthenticated(isAuth);
      setLoading(false);
    };
    
    checkAuth();
    
    // Set up auth state listener
    const unsubscribe = FirebaseService.onAuthStateChanged((user) => {
      setAuthenticated(!!user);
      setLoading(false);
    });
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return authenticated ? children : <Navigate to="/login" />;
};

// Auth Guard for Login/Register pages - redirects to dashboard if already logged in
const AuthGuard = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  
  useEffect(() => {
    const checkAuth = () => {
      const isAuth = FirebaseService.isAuthenticated();
      setAuthenticated(isAuth);
      setLoading(false);
    };
    
    checkAuth();
    
    const unsubscribe = FirebaseService.onAuthStateChanged((user) => {
      setAuthenticated(!!user);
      setLoading(false);
    });
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return authenticated ? <Navigate to="/admin/dashboard" /> : children;
};

function App() {
  const [appLoading, setAppLoading] = useState(true);

  // Initialize Firestore with sample data if empty and check auth persistence
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize Firestore with sample data
        await FirebaseService.initializeFirestoreWithSampleData();
        
        // Give a moment for Firebase auth to initialize
        setTimeout(() => {
          setAppLoading(false);
        }, 1000);
      } catch (error) {
        // Error initializing app
        setAppLoading(false);
      }
    };
    
    initializeApp();
  }, []);
  
  if (appLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ 
        minHeight: '100vh',
        bgcolor: 'transparent',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Verify />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/verify-organization" element={<VerifyOrganization />} />
            <Route path="/verify-personnel" element={<VerifyPersonnel />} />
            
            {/* Auth Routes - redirect to dashboard if already logged in */}
            <Route path="/login" element={
              <AuthGuard>
                <Login />
              </AuthGuard>
            } />
            {/* Register route removed for security */}
            <Route path="/forgot-password" element={
              <AuthGuard>
                <ForgotPassword />
              </AuthGuard>
            } />
            
            {/* Protected Routes - require authentication */}
            <Route path="/admin/dashboard" element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/add-certificate" element={
              <ProtectedRoute>
                <AddCertificate />
              </ProtectedRoute>
            } />
            <Route path="/admin/accredited-organizations" element={
              <ProtectedRoute>
                <Navigate to="/admin/dashboard?tab=organizations" replace />
              </ProtectedRoute>
            } />
            <Route path="/admin/add-accredited-organization" element={
              <ProtectedRoute>
                <AddAccreditedOrganization />
              </ProtectedRoute>
            } />
            <Route path="/admin/verified-personnel" element={
              <ProtectedRoute>
                <Navigate to="/admin/dashboard?tab=personnel" replace />
              </ProtectedRoute>
            } />
            <Route path="/admin/add-verified-personnel" element={
              <ProtectedRoute>
                <AddVerifiedPersonnel />
              </ProtectedRoute>
            } />
            <Route path="/admin/certificates" element={
              <ProtectedRoute>
                <Navigate to="/admin/dashboard?tab=certificates" replace />
              </ProtectedRoute>
            } />
            
            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </Box>
    </ThemeProvider>
  );
}

export default App;
